// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
import React, { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Box } from 'grommet'
import { useNavigate, useParams } from 'react-router-dom'
import { StatusCritical } from 'grommet-icons'

import {
  Notification,
  Anchor,
  Typography,
  Wizard
} from '../../../../components'
import { validateForm } from '../../../../utils/validation-utils'
import {
  authorizationModeEnum,
  mspApplicationEnum,
  policyTypeEnum,
  ssoConnectionTypeEnum
} from '../../../../utils/constants'
import { post } from '../../../../utils/api-utils'
import { isMSP } from '../../../../utils/feature-flag-utils'
import RecoveryAccount from '../../sso-profiles/components/add-sso-profile/steps/recovery-account/RecoveryAccount'
import { validateRecoveryUser } from '../../sso-profiles/components/add-sso-profile/steps/utils/validation-utils'

import General from './steps/General'
import { validateGeneralFields } from './steps/utils/validation-utils'
import ReviewAndCreate from './steps/ReviewAndCreate'

const CreateAuthPolicyWizard = () => {
  const navigate = useNavigate()
  const { t } = useTranslation(['common', 'iam'])
  const { id } = useParams()
  const [errorMsg, setErrorMsg] = useState(null)

  const actionOnExitFn = () => {
    navigate('/manage-account/organization/sso-configurations')
  }

  const getAuthorizationMode = (formValues) => {
    let targetsKeyName
    switch (formValues.general.authorizationMode) {
      case authorizationModeEnum.AUTHORIZATION_MODE:
        targetsKeyName = 'authorizationMode'
        break
      case authorizationModeEnum.AUTHENTICATION_ONLY_MODE:
        targetsKeyName = 'authenticationOnlyMode'
        break
      default:
        break
    }
    let targetsValue
    if (isMSP()) {
      switch (formValues.general.mspApplication.toString()) {
        case `${mspApplicationEnum.ORG_WORKSPACES}`:
          targetsValue = { [targetsKeyName]: { organizationWorkspaces: ['*'] } }
          break
        case `${mspApplicationEnum.MANAGED_ORGANIZATIONS}`:
          targetsValue = {
            [targetsKeyName]: {
              managedOrganizations: ['*']
            }
          }
          break
        case `${mspApplicationEnum.ORG_WORKSPACES},${mspApplicationEnum.MANAGED_ORGANIZATIONS}`:
          targetsValue = {
            [targetsKeyName]: {
              managedOrganizations: ['*'],
              organizationWorkspaces: ['*']
            }
          }
          break
        default:
          break
      }
    } else {
      targetsValue = {
        [targetsKeyName]: {
          organizationWorkspaces: ['*']
        }
      }
    }
    return targetsValue
  }

  const actionOnSubmit = async (formValues, setFormError) => {
    const postCallParams = {
      policyType: Object.keys(policyTypeEnum).find(
        (key) => policyTypeEnum[key] === formValues.general.policyType
      ),
      condition: {
        domain: formValues.general.domain
      },
      targets: getAuthorizationMode(formValues),
      action: {
        type: Object.keys(ssoConnectionTypeEnum).find(
          (key) =>
            ssoConnectionTypeEnum[key] === formValues.general.ssoConnectionType
        ),
        authSource: formValues.general.ssoConnectionID || ''
      }
    }
    post('/identity/v1alpha1/sso-authentication-policies', postCallParams).then(
      () => {
        navigate('/manage-account/organization/sso-configurations', {
          state: {
            notification: t(
              'Creating authentication policy **{{domainName}}**.',
              { domainName: formValues.general.domain }
            )
          } // TODO: Translation for API success response
        })
      },
      (error) => {
        // TODO: Remove after confirmation from UX about how to handle the error scenarios
        console.log(error)
        navigate('/manage-account/organization/sso-configurations', {
          state: {
            notificationError: t(
              'Unable to create authentication policy for **{{domainName}}**.',
              { domainName: formValues.general.domain }
            )
          } // TODO: Translation for API error response
        })
      }
    )
    setFormError('')
  }

  const wizardButtons = {
    next: {
      label: t('common:button.next'),
      submitForm: false
    },
    finish: id
      ? t('iam:sso_profiles.view_edit_create.save_changes')
      : t('common:create')
  }

  const validateFormData = () => {
    return new Promise((resolve) => {
      resolve()
    })
  }
  const steps = [
    {
      title: t('iam:sso_configurations.create_auth_policy_wizard.general'),
      childComponents: <General />,
      description: (
        <Typography
          type="text"
          size="large"
          weight={400}
          testId="general-description"
        >
          <Trans
            i18nKey="iam:sso_configurations.create_auth_policy_wizard.step_1_description"
            t={t}
          >
            <Anchor
              testId="create_auth_policy_learn_anchor"
              label={t('common:learn_more')}
              weight="bold"
              onClick={() => dispatchEvent(new Event('context-help'))}
            />
          </Trans>
        </Typography>
      ),
      validateForm: (formValues) => {
        return validateForm(formValues, t, validateGeneralFields)
      }
    },
    {
      title: t('iam:sso_profiles.view_edit_create.recovery_account'),
      description: (
        <Typography type="text" size="large" weight={400}>
          <Trans
            i18nKey="iam:sso_profiles.view_edit_create.recovery_account_description"
            t={t}
          >
            <Anchor
              testId="recovery_account_learn_anchor"
              label={t('common:learn_more')}
              weight="bold"
              onClick={() => dispatchEvent(new Event('context-help'))}
            />
          </Trans>
        </Typography>
      ),
      childComponents: <RecoveryAccount />,
      validateForm: (formValues) => {
        return validateForm(formValues, t, validateRecoveryUser)
      }
    },
    {
      title: id
        ? t('iam:sso_profiles.view_edit_create.review_save_changes')
        : t('iam:sso_profiles.view_edit_create.review_create'),
      description: t('iam:sso_profiles.view_edit_create.review_description'),
      childComponents: <ReviewAndCreate />,
      validateForm: () => validateFormData()
    }
  ]

  return (
    <>
      <Box>
        <Wizard
          testId="create-authentication-policy-wizard"
          title={t(
            'iam:sso_configurations.create_auth_policy_wizard.wizard_title'
          )}
          formDefaultValues={{ recovery_user: { recoveryUserEnabled: true } }}
          actionOnSubmit={(formValues, setFormError) => {
            actionOnSubmit(formValues, setFormError, t)
          }}
          // disableFinishOnSubmit={disableFinishOnSubmit}
          buttonLabels={wizardButtons}
          actionOnExit={() => actionOnExitFn()}
          cancelStrings={{
            continueLabel: t(
              'iam:sso_profiles.view_edit_create.stay_in_wizard'
            ),
            exitLabel: t('iam:sso_profiles.view_edit_create.leave_wizard'),
            heading: t(
              'iam:sso_profiles.view_edit_create.leave_without_finishing'
            ),
            text: t('iam:sso_profiles.view_edit_create.lose_progress_warning', {
              action: id
                ? t('iam:sso_profiles.view_edit_create.update')
                : t('common:create')
            })
          }}
          steps={steps}
        />
        {errorMsg && (
          <Notification
            backgroundColor="status-critical"
            icon={<StatusCritical color="text-strong" />}
            onClose={() => setErrorMsg(null)}
            text={errorMsg}
            testId="api-notification"
            position="top"
          />
        )}
      </Box>
    </>
  )
}

export default CreateAuthPolicyWizard
